// File: `useAppointmentMixinBase.js`
import { ref, computed, onMounted, watch } from 'vue';
import { isNumber } from 'class-validator';
import { DateTime } from 'luxon';
import {
  AppointmentStatus,
  CustomFieldType,
  formatDateTimeWithMilitarySupport,
  formatPhoneNumber as formatPhoneNumberCore,
  getCustomFieldFormattedValue,
  getDefaultCustomFieldName,
  getFormattedTime,
  isCancelAllowed,
  LuxonDateTimeFormats,
  transformForUserFriendlyTimezone
} from '@nova/core';
import { useNotify } from '@/composables/index';
import { useStore } from 'vuex';
import util from '@satellite/plugins/util';
import { useRoute } from 'vue-router';
import appointmentService from '@satellite/services/appointment-service.js';
import notificationsHelper from '@satellite/helpers/notifications.js';
import { useI18n } from 'vue-i18n';

export const appointmentHelpersProps = {
  appointment: {
    type: Object,
    required: true
  }
};

export default function useAppointmentHelpers(appointment) {
  const showDetailsDialog = ref(false);
  const customFields = ref({});
  const store = useStore();
  const notify = useNotify();
  const route = useRoute();
  const { t } = useI18n();

  const lockRefNumEditing = ref(false);

  const timezone = computed(() => appointment?.dock?.warehouse?.timezone ?? null);

  const useMilitaryTime = computed(() => {
    return store.getters['Settings/isMilitaryTimeEnabled'](appointment?.dock?.warehouse);
  });

  const canCancel = computed(() => {
    let canCancel = false;
    if (appointment?.status) {
      canCancel = isCancelAllowed(appointment.status, appointment.type);
    }
    return canCancel;
  });

  const isCancelled = computed(() => {
    return isStatusMatch(AppointmentStatus.Cancelled);
  });

  const isRequested = computed(() => {
    return isStatusMatch(AppointmentStatus.Requested);
  });

  const isCompleted = computed(() => {
    return isStatusMatch(AppointmentStatus.Completed);
  });

  const isScheduled = computed(() => {
    return isStatusMatch(AppointmentStatus.Scheduled);
  });

  const isNoShow = computed(() => {
    return isStatusMatch(AppointmentStatus.NoShow);
  });

  const cancelButtonIcon = computed(() => {
    return isCancelled.value ? 'mdi-alert' : 'mdi-delete-outline';
  });

  const cancelAppointmentLabel = computed(() => {
    let label = 'Cancel Appointment';
    if (appointment?.statusTimeline) {
      const cancelledTime = appointment.statusTimeline[AppointmentStatus.Cancelled];
      if (cancelledTime) {
        const cancelledDateTime = formatDateTimeWithMilitarySupport(
          cancelledTime,
          timezone.value,
          LuxonDateTimeFormats.MonthDayYearSlashedTimeAMPMCompact,
          useMilitaryTime.value,
          LuxonDateTimeFormats.MonthDayYearSlashedTime24
        );
        label = `This appointment was cancelled on ${cancelledDateTime}`;
      }
    }
    return label;
  });

  const isPastAppointment = computed(() => {
    const now = DateTime.now().setZone(timezone.value);
    const appointmentStartDateTime = DateTime.fromISO(appointment.start).setZone(timezone.value);
    return appointmentStartDateTime < now;
  });

  const user = computed(() => {
    return appointment.user;
  });

  const company = computed(() => {
    return user.value.company;
  });

  const companyName = computed(() => {
    return company.value?.name;
  });

  const scac = computed(() => {
    return appointment.user?.company?.scac || '----';
  });

  const formattedAppointmentDate = computed(() => {
    return formatDateTimeWithMilitarySupport(
      appointment.start,
      timezone.value,
      LuxonDateTimeFormats.LongDateShortMonth,
      useMilitaryTime.value,
      LuxonDateTimeFormats.LongDateShortMonth
    );
  });

  const formattedAppointmentDateTime = computed(() => {
    const interval = getAppointmentInterval(appointment);
    return `${formattedAppointmentDate.value} • ${interval}`;
  });

  const appointmentDateTimeHeader = computed(() => {
    const interval = getAppointmentInterval(appointment);
    return `${formattedAppointmentDate.value}<span class='mx-2 d-inline-block'>&bull;</span>${interval}`;
  });

  const refNumLabel = computed(() => {
    return store.getters['Settings/settingValue'](
      'referenceNumberDisplayName',
      appointment?.dock?.warehouse?.settings || null
    );
  });

  const currentTimestamp = computed(() => {
    return formatDateTimeWithMilitarySupport(
      DateTime.now().toISO(),
      timezone.value,
      LuxonDateTimeFormats.MonthDayYearSlashedTimeAMPM,
      useMilitaryTime.value,
      LuxonDateTimeFormats.MonthDayYearSlashedTime24
    );
  });

  const warehouse = computed(() => {
    return appointment.dock?.warehouse;
  });

  const warehouseAddress = computed(() => {
    return `${warehouse.value.street}, ${warehouse.value.city}, ${warehouse.value.state} ${warehouse.value.zip}`;
  });

  const appointmentNotes = computed(() => {
    return appointment.notes?.length > 0 ? appointment.notes : 'N/A';
  });

  const appointmentTags = computed(() => {
    return appointment.tags?.length > 0 ? appointment.tags.join(', ') : 'N/A';
  });

  const dock = computed(() => {
    return appointment.dock;
  });

  const loadType = computed(() => {
    return appointment.loadType;
  });

  const statusTimeline = computed(() => {
    return appointment.statusTimeline;
  });

  const arrivalTime = computed(() => {
    return statusTimeline.value[AppointmentStatus.Arrived];
  });

  const formattedArrivalTime = computed(() => {
    return arrivalTime.value
      ? formatDateTimeWithMilitarySupport(
          arrivalTime.value,
          timezone.value,
          LuxonDateTimeFormats.MonthDayYearSlashedTimeAMPM,
          useMilitaryTime.value,
          LuxonDateTimeFormats.MonthDayYearSlashedTime24
        )
      : 'Waiting for carrier to arrive';
  });

  const completedTime = computed(() => {
    return statusTimeline.value[AppointmentStatus.Completed];
  });

  const formattedCompletedTime = computed(() => {
    return completedTime.value
      ? formatDateTimeWithMilitarySupport(
          completedTime.value,
          timezone.value,
          LuxonDateTimeFormats.MonthDayYearSlashedTimeAMPM,
          useMilitaryTime.value,
          LuxonDateTimeFormats.MonthDayYearSlashedTime24
        )
      : 'Not completed yet';
  });

  const noShow = computed(() => {
    return appointment.status === AppointmentStatus.NoShow;
  });

  const arrivalTimeDiff = computed(() => {
    let readableDuration = '';
    if (arrivalTime.value) {
      readableDuration = util.getReadableDuration(
        appointment.start,
        arrivalTime.value,
        timezone.value,
        ' late'
      );
    }
    return readableDuration;
  });

  const completedTimeDiff = computed(() => {
    let readableDuration = '';
    if (completedTime.value) {
      readableDuration = util.getReadableDuration(
        arrivalTime.value,
        completedTime.value,
        timezone.value,
        ' dwell'
      );
    }
    return readableDuration;
  });

  const canCopy = computed(() => {
    return Boolean(navigator?.clipboard);
  });

  const isStatusMatch = status => {
    return appointment?.status === status;
  };

  const formatDateTime = (dateTime, format, applyTimezone = false) => {
    if (!applyTimezone) {
      return getFormattedTime(dateTime, format);
    }
    return DateTime.fromISO(dateTime).setZone(timezone.value).toFormat(format).toUpperCase();
  };

  const getAbbreviatedTimezoneName = appointment => {
    return formatDateTimeWithMilitarySupport(
      appointment.start,
      timezone.value,
      LuxonDateTimeFormats.AbbreviatedNamedOffset,
      false,
      LuxonDateTimeFormats.AbbreviatedNamedOffset
    );
  };

  const getAppointmentInterval = (appointment, applyTimezone = true) => {
    const tz = applyTimezone ? timezone.value : null;

    const start = formatDateTimeWithMilitarySupport(
      appointment.start,
      tz,
      LuxonDateTimeFormats.Extended12HrTimeAMPM,
      useMilitaryTime.value,
      LuxonDateTimeFormats.Extended24HrTime
    );
    const end = formatDateTimeWithMilitarySupport(
      appointment.end,
      tz,
      LuxonDateTimeFormats.Extended12HrTimeAMPM,
      useMilitaryTime.value,
      LuxonDateTimeFormats.Extended24HrTime
    );

    let intervalString = `${start} - ${end}`;
    if (applyTimezone) {
      const timezone = getAbbreviatedTimezoneName(appointment);
      intervalString += ` (${timezone})`;
    }
    return transformForUserFriendlyTimezone(intervalString, tz);
  };

  const handleApptIdURLQuery = async () => {
    const routeAppointmentId = route.query.appointmentId;
    if (routeAppointmentId && routeAppointmentId !== store.getters['Calendar/selectedEvent']?.id) {
      await fetchAndOpenAppt(routeAppointmentId);
    }
  };

  const fetchAndOpenAppt = async apptId => {
    const response = await store.dispatch('Calendar/getEvent', { appointmentId: apptId });
    store.commit('Calendar/setSelectedEvent', response);
    showDetailsDialog.value = true;
  };

  const getCustomFieldValue = customField => {
    const shouldGetValue =
      Boolean(customField.value) || customField?.value === false || isNumber(customField.value);
    if (shouldGetValue) {
      return getCustomFieldFormattedValue(customField, {
        [CustomFieldType.Document]: { generateLink: true },
        [CustomFieldType.MultiDocument]: { generateLink: true },
        [CustomFieldType.Timestamp]: {
          timezone: timezone.value,
          formatAsMilitary: useMilitaryTime.value
        }
      });
    }
  };

  const copyApptId = () => {
    if (canCopy.value) {
      navigator.clipboard.writeText(appointment.id);
      notify('Copied to Clipboard!');
    }
  };

  // TODO: This is also above as a computed property - figure out which one makes most sense
  // This takes a warehouse, the other computes the address based on the appointment's warehouse
  const getWarehouseAddress = warehouse => {
    return `${warehouse.street}, ${warehouse.city}, ${warehouse.state} ${warehouse.zip}`;
  };

  const getUserName = user => {
    return `${user.firstName} ${user.lastName}`;
  };

  const setCustomFieldsRows = () => {
    customFields.value = {};
    if (Array.isArray(appointment.customFields)) {
      appointment.customFields.map((field, index) => {
        const fieldName = getDefaultCustomFieldName(field);
        const fieldTemplate = appointment.dock.warehouse.customApptFieldsTemplate?.find(
          template => fieldName === getDefaultCustomFieldName(template)
        );

        if (field.value || field.value === false || isNumber(field.value)) {
          customFields.value[fieldName] = {
            label: field.label,
            value: getCustomFieldValue(field),
            name: fieldName,
            tdClass: 'pb-4',
            key: `custom-field-${index}`,
            hiddenFromCarrier: Boolean(fieldTemplate?.hiddenFromCarrier),
            requiredForCarrier: Boolean(fieldTemplate?.requiredForCarrier),
            requiredForWarehouse: Boolean(fieldTemplate?.requiredForWarehouse),
            type: field.type
          };
        }
      });
    }
  };

  const formatPhoneNumber = value => {
    return formatPhoneNumberCore(value);
  };

  const showCustomField = customField => {
    if (!customField) {
      return false;
    }

    const isDocument = [CustomFieldType.Document, CustomFieldType.MultiDocument].includes(
      customField.type
    );

    if (isDocument) {
      return false;
    }

    return Boolean(customField.value) || customField.value === false || isNumber(customField.value);
  };

  const getRowVal = val => {
    if (val === '' || val === undefined || val === null) {
      return '<span>----</span>';
    }
    return val;
  };

  const getAppointmentDateTimeHeader = appointment => {
    if (appointment?.id) {
      const interval = getAppointmentInterval(appointment, false);
      return `${formattedAppointmentDate.value}<span class='mx-2 d-inline-block'>&bull;</span>${interval}`;
    }
  };

  const getFullAppointmentTimezone = appointment => {
    if (appointment?.id) {
      return DateTime.fromISO(appointment.start).setZone(timezone.value).zoneName.replace('_', ' ');
    }
  };

  const updateAppointmentRefNumbers = async newRefNumbers => {
    lockRefNumEditing.value = true;
    try {
      const response = await appointmentService.updateAppointment(
        appointment.id,
        {
          refNumbers: newRefNumbers
        },
        { includeMetaData: true },
        { suppressNotification: true }
      );
      notificationsHelper.showAppointmentSuccessNotification({
        appointmentData: response.data?.data,
        notifyFn: notify,
        defaultMessage: t(
          'appointment.dialogs.appointmentDetailsDialog.messages.appointmentHasBeenUpdated'
        ),
        isUpdate: true
      });

      if (response?.data) {
        await store.dispatch('Appointments/triggerSocketEvent', {
          response,
          appointment
        });
      }
    } catch (err) {
      notify(
        err?.response?.data?.message ??
          t(
            'appointment.dialogs.appointmentDetailsDialog.messages.anErrorOccurredWhileUpdatingTheAppointment'
          ),
        'error',
        {
          title: notificationsHelper.getNotificationTitleByRespCode(err?.response?.data?.errorCode)
        }
      );
    } finally {
      lockRefNumEditing.value = false;
    }
  };

  const normalizeRefNumbers = (refNumbers, refNumber = null) => {
    const raw =
      Array.isArray(refNumbers) && refNumbers.length
        ? refNumbers
        : refNumber != null
          ? String(refNumber).split(',')
          : [];

    const cleaned = raw.map(v => String(v).trim()).filter(Boolean);

    return Array.from(new Set(cleaned));
  };

  onMounted(() => {
    handleApptIdURLQuery();
  });

  watch(
    () => appointment,
    () => {
      setCustomFieldsRows();
    }
  );

  return {
    showDetailsDialog,
    customFields,
    useMilitaryTime,
    canCancel,
    isCancelled,
    isRequested,
    isCompleted,
    isScheduled,
    isNoShow,
    cancelButtonIcon,
    cancelAppointmentLabel,
    isPastAppointment,
    user,
    company,
    companyName,
    scac,
    formattedAppointmentDate,
    formattedAppointmentDateTime,
    appointmentDateTimeHeader,
    refNumLabel,
    currentTimestamp,
    warehouse,
    warehouseAddress,
    appointmentNotes,
    appointmentTags,
    dock,
    loadType,
    statusTimeline,
    arrivalTime,
    formattedArrivalTime,
    completedTime,
    formattedCompletedTime,
    noShow,
    timezone,
    arrivalTimeDiff,
    completedTimeDiff,
    canCopy,
    isStatusMatch,
    formatDateTime,
    getAbbreviatedTimezoneName,
    getAppointmentInterval,
    handleApptIdURLQuery,
    fetchAndOpenAppt,
    getCustomFieldValue,
    copyApptId,
    getWarehouseAddress,
    getUserName,
    setCustomFieldsRows,
    formatPhoneNumber,
    showCustomField,
    getRowVal,
    getAppointmentDateTimeHeader,
    getFullAppointmentTimezone,
    updateAppointmentRefNumbers,
    lockRefNumEditing,
    normalizeRefNumbers
  };
}
